import React, { useState } from "react";
import { Dropdown, Tag, Tooltip } from "antd";
import type { MenuProps } from "antd";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

import IssueKeyBadge from "../../issues/components/IssueKeyBadge";
import UserAvatar from "../../components/UserAvatar";
import { ISSUE_TYPES, PRIORITY_OPTIONS, STATUS_CATEGORY_COLORS } from "../../constants/projects.constants";

import type { Issue, Sprint, SprintState, Status } from "../../../../core/data/redux/projects/types";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";

interface SprintRowProps { sprint: Sprint; issues: Issue[]; statuses: Status[]; projectKey: string; isReorderable: boolean; canManageSprint?: boolean; canDeleteSprint?: boolean; onStart: (s: Sprint) => void; onComplete: (s: Sprint) => void; onEdit: (s: Sprint) => void; onDelete: (s: Sprint) => void; }

const STATE_CONFIG: Record<SprintState, { color: string; bg: string; dot: string; tag: string }> = {
  PLANNED: { color: "#64748b", bg: "#f1f5f9", dot: "#94a3b8", tag: "default" },
  ACTIVE: { color: "#059669", bg: "#ecfdf5", dot: "#10b981", tag: "green" },
  COMPLETED: { color: "#2563eb", bg: "#eff6ff", dot: "#3b82f6", tag: "blue" },
};

const GRADIENT: Record<SprintState, string> = {
  ACTIVE: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
  PLANNED: "linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)",
  COMPLETED: "linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)",
};

const SprintRow: React.FC<SprintRowProps> = ({ sprint, issues, statuses, projectKey, isReorderable, canManageSprint = false, canDeleteSprint = false, onStart, onComplete, onEdit, onDelete }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `sprint-${sprint.id}`, data: { sprintRow: true, sprintId: sprint.id }, disabled: !isReorderable });
  const droppable = useDroppable({ id: `sprint-target-${sprint.id}`, data: { sprintTarget: true, sprintId: sprint.id } });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" };
  const itemIds = issues.map((i) => `issue-${i.id}`);
  const sc = STATE_CONFIG[sprint.state];

  const menuItems: MenuProps["items"] = [
    canManageSprint && sprint.state === "PLANNED" && { key: "edit", icon: <i className="ti ti-edit" style={{ color: "#f59e0b" }} />, label: "Edit", onClick: () => onEdit(sprint) },
    canDeleteSprint && sprint.state === "PLANNED" && { key: "delete", icon: <i className="ti ti-trash" />, danger: true, label: "Delete", onClick: () => onDelete(sprint) },
  ].filter(Boolean) as MenuProps["items"];

  const dateRange = (() => { const s = humanReadableDate(sprint.start_date as any); const e = humanReadableDate(sprint.end_date as any); if (s && e) return `${s} – ${e}`; if (s) return `Starts ${s}`; if (e) return `Ends ${e}`; return null; })();
  const setRefs = (node: HTMLDivElement | null) => { setNodeRef(node); droppable.setNodeRef(node); };

  return (
    <div ref={setRefs} style={style} className="card mb-3 border-0 overflow-hidden" {...({} as any)}
      onMouseEnter={(e) => {
        if (isDragging) return;
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)";
      }}
      onMouseLeave={(e) => {
        if (isDragging) return;
        e.currentTarget.style.boxShadow = "0 1px 6px rgba(0,0,0,0.05)";
      }}
    >
      <div style={{ height: 4, background: GRADIENT[sprint.state] }} />
      <div className="card-header bg-white border-bottom" style={{ padding: "12px 16px" }}>
        <div className="d-flex align-items-center gap-2">
          {isReorderable && (<button type="button" className="btn btn-sm btn-light" style={{ cursor: "grab", borderRadius: 6 }} {...attributes} {...listeners} title="Drag to reorder"><i className="ti ti-grip-vertical" /></button>)}
          <button type="button" className="btn btn-sm btn-link p-0 text-dark" onClick={() => setCollapsed((c) => !c)} title={collapsed ? "Expand" : "Collapse"}><i className={collapsed ? "ti ti-chevron-right" : "ti ti-chevron-down"} /></button>
          <span className="fw-semibold" style={{ fontSize: 14 }}>{sprint.name}</span>
          <Tooltip title={`State: ${sprint.state}`}>
            <span className="d-inline-flex align-items-center gap-1" style={{ background: sc.bg, color: sc.color, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: sc.dot, display: "inline-block" }} />{sprint.state}
            </span>
          </Tooltip>
          {dateRange && (<Tooltip title={dateRange}><span className="text-muted small"><i className="ti ti-calendar me-1" />{dateRange}</span></Tooltip>)}
          <span className="text-muted small">({issues.length} issue{issues.length === 1 ? "" : "s"})</span>
          <div className="ms-auto d-flex align-items-center gap-2">
            {canManageSprint && sprint.state === "PLANNED" && (<button type="button" className="btn btn-sm btn-purple" style={{ borderRadius: 6 }} onClick={() => onStart(sprint)} title="Start sprint">Start Sprint</button>)}
            {canManageSprint && sprint.state === "ACTIVE" && (<button type="button" className="btn btn-sm btn-success" style={{ borderRadius: 6 }} onClick={() => onComplete(sprint)}>Complete Sprint</button>)}
            {menuItems && menuItems.length > 0 && (<Dropdown menu={{ items: menuItems }} trigger={["click"]}><button type="button" className="btn btn-sm btn-light" style={{ borderRadius: 6 }}><i className="ti ti-dots-vertical" /></button></Dropdown>)}
          </div>
        </div>
        {sprint.goal && !collapsed && (<div className="text-muted small mt-2" style={{ paddingLeft: isReorderable ? 40 : 28 }}><i className="ti ti-target me-1" />{sprint.goal}</div>)}
      </div>
      {!collapsed && (
        <div className="card-body p-0 rounded-3">
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            {issues.length === 0 ? (
              <div
                className="text-center text-muted small d-flex align-items-center justify-content-center"
                style={{
                  minHeight: 80,
                  margin: 8,
                  background: droppable.isOver ? "rgba(59, 130, 246, 0.08)" : "transparent",
                  borderRadius: 8,
                  border: droppable.isOver ? "2px dashed #3B82F6" : "2px dashed #e2e8f0",
                  transition: "all .15s",
                }}
              >
                <i className="ti ti-arrows-move me-1" />Drag issues here to add them to this sprint
              </div>
            ) : (
              <ul className="list-unstyled mb-0">
                {issues.map((issue) => (<SprintIssueRow key={issue.id} issue={issue} projectKey={projectKey} statuses={statuses} />))}
              </ul>
            )}
          </SortableContext>
        </div>
      )}
    </div>
  );
};

interface SprintIssueRowProps { issue: Issue; projectKey: string; statuses: Status[]; }

const SprintIssueRow: React.FC<SprintIssueRowProps> = ({ issue, projectKey, statuses }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `issue-${issue.id}`, data: { issueId: issue.id, issueRow: true } });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 };
  const handleStyle: React.CSSProperties = { cursor: isDragging ? "grabbing" : "grab", fontSize: 16, color: "#94a3b8", flexShrink: 0, padding: "6px 4px", userSelect: "none", WebkitUserSelect: "none", touchAction: "none" };
  const typeMeta = ISSUE_TYPES.find((t) => t.value === issue.type);
  const priorityMeta = PRIORITY_OPTIONS.find((p) => p.value === issue.priority);
  const status = statuses.find((s) => Number(s.id) === Number(issue.status_id));

  return (
    <li ref={setNodeRef} style={style}
      className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
      onMouseEnter={(e) => {
        if (isDragging) return;
        e.currentTarget.style.background = "#f8fafc";
      }}
      onMouseLeave={(e) => {
        if (isDragging) return;
        e.currentTarget.style.background = "transparent";
      }}
    >
      <span {...attributes} {...listeners} style={handleStyle} className="drag-grip" title="Drag to move" aria-label="Drag handle">
        <i className="ti ti-grip-vertical" />
      </span>
      {typeMeta && (<Tooltip title={typeMeta.label}><span className="d-inline-block rounded-circle" style={{ width: 8, height: 8, background: typeMeta.color }} /></Tooltip>)}
      <IssueKeyBadge projectKey={projectKey} issueKey={issue.issue_key} />
      <span className="flex-grow-1 text-truncate" style={{ fontSize: 13 }}>{issue.title}</span>
      {priorityMeta && (<Tooltip title={`Priority: ${priorityMeta.label}`}><span className="d-inline-flex align-items-center gap-1" style={{ background: "#f8fafc", padding: "2px 6px", borderRadius: 4, fontSize: 10, color: priorityMeta.color, fontWeight: 600 }}><span className="d-inline-block rounded-circle" style={{ width: 6, height: 6, background: priorityMeta.color }} />{priorityMeta.label}</span></Tooltip>)}
      {status && (<Tooltip title={`Status: ${status.name}`}><Tag color="default" style={{ background: status.color || STATUS_CATEGORY_COLORS[status.category], color: "#fff", border: "none", margin: 0, fontSize: 10.5 }}>{status.name}</Tag></Tooltip>)}
      {issue.assignee_snapshot?.name && (<Tooltip title={issue.assignee_snapshot.name}><span><UserAvatar name={issue.assignee_snapshot.name} img={issue.assignee_snapshot.img} size={22} /></span></Tooltip>)}
    </li>
  );
};

export default SprintRow;
